import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    useFavoritesMutation,
    useRemoveFavoritesMutation,
    useFavoriteStatusQuery
} from '../slices/usersApiSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartOutline } from '@awesome.me/kit-367a27fd96/icons/classic/thin';

const Favorite = ({ productId, initialIsFavorite = false }) => {
    const navigate = useNavigate();
    const { userInfo } = useSelector((state) => state.auth);
    const [localIsFavorite, setLocalIsFavorite] = useState(initialIsFavorite);
    const [isProcessing, setIsProcessing] = useState(false);

    const {
        data: favoriteStatus,
        isLoading: isCheckingStatus,
        refetch
    } = useFavoriteStatusQuery(productId, {
        pollingInterval: 0,
        skip: !productId || !userInfo
    });

    const isMobile = window.innerWidth <= 430;

    const [addToFavorites, { isLoading: isAdding }] = useFavoritesMutation();
    const [removeFromFavorites, { isLoading: isRemoving }] = useRemoveFavoritesMutation();

    useEffect(() => {
        if (favoriteStatus) {
            const serverIsFavorite = favoriteStatus.message === 'Product is in favorites' ||
                favoriteStatus.isFavorite === true;
            setLocalIsFavorite(serverIsFavorite);
        }
    }, [favoriteStatus]);

    const isDisabled = isProcessing || isAdding || isRemoving || isCheckingStatus;

    const handleUnauthenticatedClick = () => {
        const currentPath = window.location.pathname;
        navigate(`/login?redirect=${currentPath}`);
    };

    const toggleFavorite = async () => {
        if (!userInfo) {
            handleUnauthenticatedClick();
            return;
        }

        if (isDisabled) return;

        const previousState = localIsFavorite;
        setLocalIsFavorite(!localIsFavorite);
        setIsProcessing(true);

        try {
            if (localIsFavorite) {
                await removeFromFavorites({ productId }).unwrap();
            } else {
                await addToFavorites({ productId }).unwrap();
            }
            await refetch();
        } catch (error) {
            console.error('❌ Error toggling favorite:', error);
            setLocalIsFavorite(previousState);
            alert(`Failed to ${previousState ? 'remove from' : 'add to'} wishlist. Please try again.`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Base button styles
    const getButtonStyles = () => ({
        background: localIsFavorite
            ? 'linear-gradient(135deg, #FD7014 0%, #f7931e 100%)'
            : 'white',
        border: localIsFavorite
            ? '2px solid #FD7014'
            : '2px solid #ccc',  // Grey when not saved
        borderRadius: '50px',
        //padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        transition: 'all 0.3s ease',
        fontWeight: '600',
        //fontSize: '14px',
        color: localIsFavorite ? 'white' : '#666',  // Grey text when not saved
        boxShadow: localIsFavorite
            ? '0 4px 12px rgba(253, 112, 20, 0.3)'
            : 'none',
        minWidth: isMobile ? '120px' : '160px',
        maxWidth: isMobile ? '120px' : '160px',
        padding: isMobile ? '8px 10px' : '12px 24px',
        fontSize: isMobile ? '12px' : '14px',
        justifyContent: 'center'
    });

    // Mobile responsive styles
    const getMobileStyles = () => ({
        '@media (max-width: 768px)': {
            minWidth: isMobile ? '120px' : '160px',
            maxWidth: isMobile ? '120px' : '160px',
            padding: isMobile ? '8px 10px' : '12px 24px',
            fontSize: isMobile ? '12px' : '14px',
        }
    });

    // Loading state
    if (userInfo && isCheckingStatus) {
        return (
            <button
                className="favorite-button-modern"
                disabled
                style={{
                    ...getButtonStyles(),
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    border: '2px solid #e0e0e0',
                }}
            >
                <FontAwesomeIcon
                    icon={faHeartOutline}
                    style={{ fontSize: '18px', color: '#999' }}
                />
                <span style={{ color: '#999' }}>Loading...</span>
            </button>
        );
    }

    // Not logged in
    if (!userInfo) {
        return (
            <button
                className="favorite-button-modern favorite-button-login"
                onClick={handleUnauthenticatedClick}
                style={{
                    background: 'white',
                    border: '2px solid #ccc',  // Grey outline
                    borderRadius: '50px',
                    //padding: '12px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: '600',
                    //fontSize: '14px',
                    color: '#666',  // Grey text
                    minWidth: isMobile ? '120px' : '160px',
                    maxWidth: isMobile ? '120px' : '160px',
                    padding: isMobile ? '8px 10px' : '12px 24px',
                    fontSize: isMobile ? '12px' : '14px',
                    justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #3E6D9C 0%, #2d5373 100%)';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = '#3E6D9C';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(62, 109, 156, 0.3)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.color = '#666';
                    e.currentTarget.style.borderColor = '#ccc';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
            >
                <FontAwesomeIcon
                    icon={faHeartOutline}
                    style={{ fontSize: '18px', transition: 'all 0.3s ease' }}
                />
                <span>Sign in</span>
            </button>
        );
    }

    // Authenticated - full functionality
    return (
        <button
            className="favorite-button-modern"
            onClick={toggleFavorite}
            disabled={isDisabled}
            style={getButtonStyles()}
            onMouseEnter={(e) => {
                if (!isDisabled) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = localIsFavorite
                        ? '0 6px 16px rgba(253, 112, 20, 0.4)'
                        : '0 4px 12px rgba(204, 204, 204, 0.3)';
                }
            }}
            onMouseLeave={(e) => {
                if (!isDisabled) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = localIsFavorite
                        ? '0 4px 12px rgba(253, 112, 20, 0.3)'
                        : 'none';
                }
            }}
        >
            <FontAwesomeIcon
                icon={localIsFavorite ? faHeart : faHeartOutline}
                className={localIsFavorite ? "fa-beat" : ""}
                style={{
                    '--fa-animation-duration': '1s',
                    fontSize: '18px',
                    transition: 'all 0.3s ease'
                }}
            />
            <span>
                {isProcessing
                    ? 'Updating...'
                    : localIsFavorite
                        ? 'Saved'
                        : 'Wishlist'
                }
            </span>
        </button>
    );
};

export default Favorite;